import type { IconButtonProps } from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import { StyledIconButton } from '@openx/components-core/lib/Button/Button.styled';
import type { IconProps } from '@phosphor-icons/react';
import { type ComponentType, type MouseEvent, useState } from 'react';

export type ChatActionButtonProps = {
  label: string;
  clickedLabel?: string;
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
  Icon: ComponentType<IconProps>;
  iconColor?: IconButtonProps['color'];
  pressedColor?: string;
  disabled?: boolean;
  pressed?: boolean | undefined;
};

export const ChatActionButton = ({
  label,
  clickedLabel,
  onClick,
  Icon,
  iconColor = 'default',
  pressedColor,
  disabled = false,
  pressed = false,
}: ChatActionButtonProps) => {
  const theme = useTheme();
  const [tooltipText, setTooltipText] = useState(label);

  const glyphColor = disabled
    ? theme.palette.action.disabled
    : pressed && pressedColor
      ? pressedColor
      : theme.palette.text.secondary;

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setTooltipText(clickedLabel ?? label);
    onClick(event);
  };

  const onMouseLeave = () => {
    setTimeout(() => setTooltipText(label), 200);
  };

  return (
    <Tooltip title={tooltipText} placement="top">
      <StyledIconButton
        key={label}
        size="large"
        onClick={handleClick}
        onMouseLeave={onMouseLeave}
        aria-label={label}
        color={iconColor}
        disabled={disabled}
        sx={{
          p: 0,
          minHeight: 36,
          minWidth: 36,
          borderRadius: 1,
          backgroundColor: pressed ? 'rgba(0,0,0,0.12)' : 'inherit',
        }}
      >
        <Icon color={glyphColor} />
      </StyledIconButton>
    </Tooltip>
  );
};
